import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "../../lib/api.js";

export interface AiModel {
  id: string;
  provider: "anthropic" | "openai";
  providerModelId: string;
  displayName: string;
  tag: string | null;
  note: string | null;
  inputPricePerMtokUsd: string;
  outputPricePerMtokUsd: string;
  active: boolean;
}

export interface AiSettings {
  businessId: string;
  enabled: boolean;
  modelId: string;
  persona: string;
  confidenceThreshold: number;
  handoffIntents: string[];
  escalationReplyMs: string;
  escalationReplyEn: string;
  monthlyCapMyr: string;
  pauseAtCap: boolean;
  rolloutStage: "shadow" | "assisted" | "auto";
}

export interface AiUsage {
  spentMyr: number;
  replies: number;
  avgCostMyr: number;
  projectedMyr: number;
  dayOfMonth: number;
  paused: boolean;
}

export interface AiSettingsPatch {
  enabled?: boolean;
  modelId?: string;
  persona?: string;
  confidenceThreshold?: number;
  escalationReplyMs?: string;
  escalationReplyEn?: string;
  monthlyCapMyr?: number;
  pauseAtCap?: boolean;
  rolloutStage?: "shadow" | "assisted" | "auto";
}

export function useAiSettings(businessId: string) {
  return useQuery<{ settings: AiSettings; usage: AiUsage }>({
    queryKey: [businessId, "ai-settings"],
    queryFn: () =>
      apiFetch<{ settings: AiSettings; usage: AiUsage }>(
        `/api/v1/ai/settings?business=${businessId}`,
      ),
  });
}

export function useAiModels() {
  return useQuery<AiModel[]>({
    queryKey: ["ai-models"],
    queryFn: async () => {
      const r = await apiFetch<{ models: AiModel[] }>("/api/v1/ai/models");
      return r.models;
    },
    staleTime: 5 * 60_000, // catalog changes under the retainer, not per click
  });
}

export function usePatchAiSettings(businessId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: AiSettingsPatch) =>
      apiFetch<{ settings: AiSettings }>(`/api/v1/ai/settings?business=${businessId}`, {
        method: "PATCH",
        body: JSON.stringify(patch),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [businessId, "ai-settings"] });
      // bot_active may have flipped (doc 13 §2.1) — refresh the switcher payload.
      qc.invalidateQueries({ queryKey: ["businesses"] });
    },
  });
}

/**
 * Per-reply cost estimate (doc 13 §3.2 assumption: ~2,500 input + ~200 output
 * tokens; MYR at ~4.7/USD). Replaced by trailing-30-day real averages once the
 * pipeline is live.
 */
export function estimateReplyMyr(m: AiModel): number {
  const usd = (2500 * Number(m.inputPricePerMtokUsd) + 200 * Number(m.outputPricePerMtokUsd)) / 1e6;
  return usd * 4.7;
}
